// ============================================================================
// v4 — "Mark": AI media buyer / CRO built into the product.
// Two looks: floating popup (default) or `inline` banner integrated in a page.
// ============================================================================
import React, { useState } from 'react';
import { v4data, GOALS } from './v4data';
import { fmt$, fmtK, fmtN, fmtX } from './v4ui';
import markPhoto from 'static/images/v4/mark.png';
import './dashboard.scss';

const short = (n) => n.split('—').pop().trim();

function velocityMessage(D) {
  const t = D.ds.accountDaily[D.to];
  const ordersGoal = Math.round((1150000 / 95) / new Date(+D.to.slice(0, 4), +D.to.slice(5, 7), 0).getDate());
  const pace = Math.round((t.orders / ordersGoal) * 100);
  const camps = D.ds.campaigns.map((c) => ({ c, m: D.campSum(c) }));
  const best = [...camps].sort((a, b) => b.m.roas - a.m.roas)[0];
  const worst = [...camps].sort((a, b) => a.m.roas - b.m.roas)[0];
  return (
    <>
      <b>We're at {pace}% of today's order goal. {fmtN(t.orders)} orders, {fmt$(t.revenue)} booked, and I'm not easing off.</b>{' '}
      I just moved more budget into <b>{short(best.c.name)}</b> at {fmtX(best.m.roas)}. It's crushing right now. I cut <b>{short(worst.c.name)}</b> at {fmtX(worst.m.roas)} because it was bleeding you dry. Approve it and I'll scale {short(best.c.name)} another 20% before midnight. Let's close the day even harder.
    </>
  );
}

function predictiveMessage(D) {
  const camps = D.ds.campaigns.map((c) => ({ c, m: D.campSum(c) }));
  const best = [...camps].sort((a, b) => b.m.roas - a.m.roas)[0];
  const toScale = camps.filter((x) => x.m.roas >= 4.5).length;
  const predBlended = D.cur.roas * 1.03;
  return (
    <>
      <b>I just ran the 7-day forecast on all {camps.length} campaigns. Blended ROAS is trending toward {fmtX(predBlended)}.</b>{' '}
      <b>{short(best.c.name)}</b> is my strongest signal at {fmtX(best.m.roas)} and still climbing. {toScale} campaigns are ready to scale today. I'm already shifting spend toward the winners. Approve it and I'll lock the plan in for the week and keep pushing.
    </>
  );
}

function overviewMessage(D) {
  const camps = D.ds.campaigns.map((c) => ({ c, m: D.campSum(c) }));
  const best = [...camps].sort((a, b) => b.m.roas - a.m.roas)[0];
  const worst = [...camps].sort((a, b) => a.m.roas - b.m.roas)[0];
  return (
    <>
      <b>Your account's humming. {fmtK(D.cur.revenue)} in attributed revenue this month at {fmtX(D.cur.roas)} blended, and I'm keeping the winners fed.</b>{' '}
      {short(best.c.name)} and your retargeting are carrying efficiency up to {fmtX(best.m.roas)}. I'm trimming the weakest spend, {short(worst.c.name)} at {fmtX(worst.m.roas)}, and moving it into what's working. Approve it and I'll keep the whole account leaning into the winners.
    </>
  );
}

function merMessage(D) {
  const mer = D.cur.roas;
  return (
    <>
      <b>Your blended MER is at {fmtX(mer)}, {mer >= GOALS.mer ? 'above' : 'below'} your {GOALS.mer}x goal.</b>{' '}
      Every dollar I spend is returning ${mer.toFixed(2)} in revenue. Retention and retargeting are pulling the blend up and I'm protecting that mix. I can push spend harder and still hold efficiency. Approve it and I'll scale while keeping MER above your goal.
    </>
  );
}

function goalstudioMessage() {
  const adspend = GOALS.revenue / GOALS.mer;
  const orders = GOALS.revenue / GOALS.aov;
  return (
    <>
      <b>I back-solved your plan. To hit {fmtK(GOALS.revenue)} this month at {fmtX(GOALS.mer)} MER, you need about {fmtK(adspend)} in spend and {fmtN(orders)} orders.</b>{' '}
      You're pacing ahead of that right now. I've already lined up the budget to get there. Approve it and I'll manage spend to these targets automatically and flag you the second anything drifts.
    </>
  );
}

export default function MarkPopup({ inline = false, context = 'velocity' }) {
  const [open, setOpen] = useState(true);
  if (!open) return null;
  const D = v4data(30);
  const message =
    context === 'predictive' ? predictiveMessage(D) :
    context === 'overview' ? overviewMessage(D) :
    context === 'mer' ? merMessage(D) :
    context === 'goalstudio' ? goalstudioMessage() :
    velocityMessage(D);

  return (
    <div className={'mark-pop' + (inline ? ' mark-inline' : '')}>
      <button className="mark-x" onClick={() => setOpen(false)} aria-label="Dismiss">✕</button>

      <div className="mark-head">
        <div className="mark-orb"><img src={markPhoto} alt="Mark" /></div>
        <div className="mark-id">
          <b>Mark</b>
          <span>AI Media Buyer · running your account</span>
        </div>
        <span className="mark-status"><i /> live</span>
      </div>

      <p className="mark-msg">{message}</p>

      <div className="mark-actions">
        <button className="mark-approve" onClick={() => setOpen(false)}>Approve the moves</button>
        <button className="mark-ghost" onClick={() => setOpen(false)}>Show me the math</button>
      </div>
    </div>
  );
}
